import { Autocomplete, Button, Card, CardContent, CardHeader, Drawer, FormHelperText, Grid, Stack, TextField, useMediaQuery } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { CreateOwnerMatchRuleInput, OwnerMatchRuleDto, UpdateOwnerMatchRuleInput } from '@/gen/api/securefile/api';
import { DataOwnerGroupResponseDto } from '@/gen/api/governance/api';
import { SimpleOption } from '../governance/search/AmbientFacilitiesContext';
import { StateErrors, StateTouched, usePlainStateFormUtils } from '../input/usePlainStateFormUtils';
import WithSave from '@/controller/WithSave';
import { ClientDto } from '@/gen/api/user';
import ReadonlyField from '../input/ReadonlyField';
import * as Yup from 'yup';
import { HttpError, ReduxState, useListContext } from 'ra-core';
import { useSelector } from 'react-redux';


interface OwnershipRuleDialogProps {
  open: boolean;
  rule: OwnerMatchRuleDto | null;
  client: ClientDto;
  onClose: () => void;
  dataOwnerGroupOptions: SimpleOption[],
}

const WINDOWS_PATH_REGEX = /^[A-Za-z]:\\/;
const UNIX_PATH_REGEX = /^\/.*/;
const PATH_ERROR = 'Folder must be a local absolute path (e.g. C:\\folder or /mnt/folder)';

const validationSchema =
  Yup.object().shape({
    pathPrefix: Yup.string().required().max(500)
      .test('is-valid-absolute-path', PATH_ERROR, (x:string) => {
        return WINDOWS_PATH_REGEX.test(x) || UNIX_PATH_REGEX.test(x)
      }),
    priority: Yup.number().required(),
    dataOwnerGroupId: Yup.string().required('Data Owner Group is required'),
  })

const OwnershipRuleDialog: FC<OwnershipRuleDialogProps> = ({
  open,
  rule,
  client,
  onClose,
  dataOwnerGroupOptions,
}) => {
  const match = useMediaQuery('(max-width: 680px)')
  const width = match ? 'calc(100vw - 50px)' : 630
  
  const [states, setStates] = useState(initS(rule, client))
  useEffect(() => {
    setStates(initS(rule, client))
  }, [client, rule])

  const { fieldProps, trySubmitValidate } = usePlainStateFormUtils(states, setStates, validationSchema)
  const occupiedPNum = useSelector<ReduxState, Set<number|undefined>>(store => {
    const resData = store.admin.resources['secure-files-ownership-rules']
    if (!resData) { return new Set() }
    const s = new Set(resData.list.ids.map(id => (resData.data[id] as OwnerMatchRuleDto).priority))
    if (states.prevData !== emptyRuleStub) {
      s.delete(states.prevData.priority)
    }
    return s
  })

  const { refetch } = useListContext()
  const handleSave = (save: (record: CreateOwnerMatchRuleInput|UpdateOwnerMatchRuleInput) => Promise<void>) => async () => {
    const v1 = await trySubmitValidate()
    if (occupiedPNum.has(v1.stage.priority)) {
      v1.errors.priority = 'Priority number should be unique'
      v1.touched.priority = true
      setStates({...v1})
      return
    }
    
    const payload : CreateOwnerMatchRuleInput = v1.stage as any

    try {
      await save(payload)
      
    } catch (error: any) {
      const hErr : HttpError = error
      setStates(v => ({...v, errors: {...v.errors, submit: hErr.message}}))
    }

    refetch?.()
    onClose();
    if (states.prevData === emptyRuleStub) {
      setStates(initS(null, client))
    }
  };

  const handleClose = () => {
    onClose();
    if (states.prevData === emptyRuleStub) {
      setStates(initS(null, client))
    }
  };

  const getEffectDataOwnerGroupItem = () => dataOwnerGroupOptions.find(x => x.key === states.stage.dataOwnerGroupId) ?? ''

  return (
    <Drawer
      anchor="right"
      // onOpen={() => {}}
      onClose={handleClose}
      open={open}
      PaperProps={{ sx: { p: 2, width: width, } }}
    >
      <Card sx={{ overflowY: 'auto', mb: 4, }} >
        <CardHeader title={states.prevData === emptyRuleStub ? 'Add Rule' : 'Edit Rule'} />
        <CardContent>
          <Grid container spacing={4} >
            <Grid item xs={12} >
              <ReadonlyField label="Computer" value={`${client.clientName}`} />
            </Grid>
            <Grid item xs={12} >
              <TextField label="Folder" fullWidth 
                {...fieldProps('pathPrefix')}
              />
              {!states.errors.pathPrefix &&
                <FormHelperText>Absolute path, e.g. C:\Data\Folder</FormHelperText>}
            </Grid>
            <Grid item xs={12} >
              <TextField label="Priority" fullWidth type="number"
                {...fieldProps('priority', (x: any) => {
                  if (!x) { return undefined }
                  let n = +x
                  if (Number.isNaN(n)) { return states.stage.priority }
                  if (n === states.stage.priority) { return n }
                  const d = n < states.stage.priority ? -1 : 1
                  // ensure priority value is unique
                  while (n > 0 && occupiedPNum.has(n)) {
                    n += d
                  }
                  if (n <= 0) { return states.stage.priority }
                  return n
                })}
              />
            </Grid>
            <Grid item xs={12} >
              <Autocomplete disableClearable={true} 
                options={dataOwnerGroupOptions}
                getOptionLabel={(option) => (option.text || option) as string}
                renderInput={(params) => 
                  <TextField label="Data Owner Group" {...params} fullWidth 
                    inputProps={{ ...params.inputProps, 'aria-label': 'Data Owner Group' }}
                    error={Boolean((states as any).__?.dataOwnerGroupId && states.errors.dataOwnerGroupId)}
                    helperText={(states as any).__?.dataOwnerGroupId && states.errors.dataOwnerGroupId}
                  />
                } 
                value={getEffectDataOwnerGroupItem() as SimpleOption}
                onChange={(event, value, reason) => {
                  if (reason === 'selectOption') {
                    const ogroup = value as DataOwnerGroupResponseDto
                    setStates(v => {
                      const v1 = {...v,
                        stage: {...v.stage, dataOwnerGroupId: ogroup.ownerGroupId! },
                        touched: {...v.touched, dataOwnerGroupId: v.prevData?.dataOwnerGroupId !== ogroup.ownerGroupId }
                      }
                      return v1
                    })
                  }
                }}
              />
            </Grid>
            {states.submit &&
              <FormHelperText sx={{ color: 'warning.main', ml: 2 }}>
                {states.submit}
              </FormHelperText>}
          </Grid>
        </CardContent>
        <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }} >
          <Button color="primary" type="button" variant="outlined"
            onClick={onClose}
          >
            Cancel
          </Button>
          <WithSave async render={({save}) =>
            <Button color="primary" type="submit" variant="contained" 
              onClick={handleSave(save)}
            >
              Save
            </Button>
          } />
        </Stack>
      </Card>
    </Drawer>
  );
};

export default OwnershipRuleDialog;

const emptyRuleStub : OwnerMatchRuleDto = Object.freeze({})

function initS(rule: OwnerMatchRuleDto | null, client: ClientDto) {
  return {
    prevData: rule ?? emptyRuleStub,
    stage: {
      computer: client.clientName!,
      clientId: client.clientId!,
      pathPrefix: rule?.pathPrefix ?? '',
      dataOwnerGroupId: rule?.dataOwnerGroupId ?? ('' as const),
      priority: rule?.priority ?? 100,
    },
    errors: { } as StateErrors<OwnerMatchRuleDto>,
    touched: { } as StateTouched<OwnerMatchRuleDto>,
    submit: null as string | null,
  }
}

